import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info } from "lucide-react";
import {
  aiPeopleSearch,
  fetchContactAvailability,
  fetchContactSearchParams,
  fetchProspectsByQuery,
  fetchSearchSuggestions,
  importFromContactOut,
  importProspectSearchEnrichedContacts,
  createProspectSearchThread,
  deleteProspectSearchThread,
  draftProspectSearchQueryFromCampaign,
  fetchProspectSearchThreadMessages,
  fetchProspectSearchThreads,
  searchProspectsByNaturalQuery,
  searchProspectsByCampaignIcp,
  sendProspectSearchChatMessage,
  loadMoreProspects,
} from "../api/apolloApi";
import { fetchProfileApi } from "@/features/profiling/api/profileApi";
import { useEnrichmentModalStore } from "@/stores/useEnrichmentModalStore";
import {
  showSuccess,
  showError,
  showWarning,
  toast,
  showErrorFromMutation,
} from "@/shared/utils/toast";
import { useMainStore } from "@/stores";

/**
 * Max number of concurrent `/prospect-profile/` prefetches kicked off after a
 * bulk contact import. The backend serializes per-company anyway, and the
 * connection pool is small — firing all N at once was saturating the pool and
 * triggering asyncpg TimeoutError.
 */
const PROFILE_FETCH_CONCURRENCY = 3;

/**
 * Run an array of task factories with at most `limit` running in parallel.
 * Each task is a `() => Promise<T>`. Returns an allSettled-shaped array.
 */
async function runWithConcurrency(limit, taskFactories) {
  const results = new Array(taskFactories.length);
  let next = 0;
  const workerCount = Math.min(limit, taskFactories.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const idx = next++;
      if (idx >= taskFactories.length) return;
      try {
        results[idx] = { status: "fulfilled", value: await taskFactories[idx]() };
      } catch (reason) {
        results[idx] = { status: "rejected", reason };
      }
    }
  });
  await Promise.all(workers);
  return results;
}

/** HTTP 200 but no contacts saved due to API-reported failures — keep import UI open. */
export function shouldKeepContactOutImportDrawerOpen(data) {
  const contacts = Array.isArray(data?.contacts) ? data.contacts : [];
  if (contacts.length > 0) return false;
  const summary = data?.summary ?? {};
  const failed = Number(summary.failed) || 0;
  const errors = Array.isArray(data?.errors) ? data.errors : [];
  return failed > 0 || errors.length > 0;
}

function formatContactOutImportFailureMessage(data) {
  const errors = Array.isArray(data?.errors) ? data.errors : [];
  const summary = data?.summary ?? {};
  const failed = Number(summary.failed) || errors.length;
  if (errors.length === 0) {
    return failed > 0
      ? `${failed} contact(s) could not be imported. Please try again or choose different prospects.`
      : "Import failed. Please try again.";
  }
  const first = errors[0];
  const msg =
    (first && typeof first === "object"
      ? first.message || first.error || first.reason || first.detail
      : null) || (typeof first === "string" ? first : null);
  const rest = errors.length - 1;
  const base =
    msg || "Something went wrong while importing contacts. Please try again.";
  return rest > 0 ? `${base} (+${rest} more)` : base;
}

async function runBulkWorkspaceImportSideEffects(queryClient, data, variables) {
  const contacts = Array.isArray(data?.contacts) ? data.contacts : [];
  const importSkipped = Array.isArray(data?.skipped) ? data.skipped : [];
  const summary = data?.summary ?? {};
  const assignment = data?.assignment ?? {};
  const assignSkipped = Array.isArray(assignment.skipped) ? assignment.skipped : [];
  const imported = contacts.length;
  const importSkippedCount =
    Number(summary.skipped) || importSkipped.length;
  const assigned =
    summary.assigned_to_campaigns ?? assignment.assigned_count ?? 0;
  const campaignIds = Array.isArray(variables?.campaign_ids)
    ? variables.campaign_ids.filter(Boolean)
    : [];
  const hasCampaigns = campaignIds.length > 0;
  const userId = useMainStore.getState().user?.id;

  if (shouldKeepContactOutImportDrawerOpen(data)) {
    showError(formatContactOutImportFailureMessage(data));
    return;
  }

  await queryClient.invalidateQueries({ queryKey: ["contacts"] });
  queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
  await queryClient.invalidateQueries({ queryKey: ["contact-groups"] });

  if (hasCampaigns) {
    campaignIds.forEach((cid) => {
      queryClient.invalidateQueries({ queryKey: ["campaign-leads", cid] });
    });
    queryClient.invalidateQueries({ queryKey: ["campaign-leads-infinite"] });
  }

  if (!hasCampaigns) {
    if (imported > 0 && importSkippedCount > 0) {
      showWarning(
        `${imported} contact${imported !== 1 ? "s" : ""} imported. ${importSkippedCount} duplicate${importSkippedCount !== 1 ? "s" : ""} skipped.`,
      );
    } else if (imported > 0) {
      showSuccess(
        `${imported} contact${imported !== 1 ? "s" : ""} imported successfully.`,
      );
    } else if (importSkippedCount > 0) {
      showWarning(
        `No new contacts imported. ${importSkippedCount} duplicate${importSkippedCount !== 1 ? "s" : ""} skipped.`,
      );
    }
  } else {
    if (imported > 0 && assignSkipped.length === 0) {
      const baseMessage =
        assigned > 0
          ? `${imported} contact${imported !== 1 ? "s" : ""} imported and ${assigned} assignment${assigned !== 1 ? "s" : ""} to campaign(s).`
          : `${imported} contact${imported !== 1 ? "s" : ""} imported and added to campaign(s).`;
      if (importSkippedCount > 0) {
        showWarning(
          `${baseMessage} ${importSkippedCount} duplicate${importSkippedCount !== 1 ? "s" : ""} skipped.`,
        );
      } else {
        showSuccess(baseMessage);
      }
    } else if (imported > 0 && assignSkipped.length > 0 && assigned > 0) {
      const skippedText =
        importSkippedCount > 0
          ? ` ${importSkippedCount} duplicate${importSkippedCount !== 1 ? "s" : ""} skipped.`
          : "";
      toast.warn(
        <div className="flex items-start gap-3 rounded-xl border border-[var(--border-color-light)] bg-[var(--bg-standard)] px-4 py-3 shadow-[0_10px_40px_-12px_rgba(37,43,55,0.18)]">
          <Info
            size={18}
            strokeWidth={1.7}
            className="shrink-0 text-[var(--text-warning)] mt-0.5"
          />
          <p className="text-[0.875rem] font-medium text-[var(--text-color)] leading-snug">
            {assignSkipped.length} contact
            {assignSkipped.length !== 1 ? "s were" : " was"} not added (already in a
            selected campaign). {assigned > 0 ? `${assigned} added elsewhere.` : ""}
            {skippedText}
          </p>
        </div>,
      );
    } else if (imported > 0 && assignSkipped.length > 0 && assigned === 0) {
      showError(
        `${assignSkipped.length} contact${assignSkipped.length !== 1 ? "s" : ""} could not be added — already in the selected campaign(s).${importSkippedCount > 0 ? ` ${importSkippedCount} duplicate${importSkippedCount !== 1 ? "s" : ""} skipped.` : ""}`,
      );
    } else if (imported === 0 && importSkippedCount > 0) {
      showWarning(
        `No new contacts imported. ${importSkippedCount} duplicate${importSkippedCount !== 1 ? "s" : ""} skipped.`,
      );
    }
  }

  // Some imported, some left out (e.g. ContactOut had nothing for an AI search result).
  const importErrors = Array.isArray(data?.errors) ? data.errors : [];
  if (imported > 0 && importErrors.length > 0) {
    showWarning(
      `${importErrors.length} contact${importErrors.length !== 1 ? "s" : ""} not added: ${formatContactOutImportFailureMessage(data)}`,
    );
  }

  const contactIds = contacts
    .map((c) => c?.contactId ?? c?.contact_id ?? c?.id)
    .filter(Boolean);
  const shouldPrefetchProfiles = Boolean(
    hasCampaigns && userId && contactIds.length > 0,
  );

  if (shouldPrefetchProfiles) {
    const primaryCampaignId = campaignIds[0] ?? null;
    console.info("[AccountResearchDebug][bulk-import] skipping account research; explicit account action required", {
      campaignIds,
      importedContactCount: contacts.length,
      contactIds,
    });

    // Build deferred task factories so the prefetches don't all kick off
    // synchronously. Without this, `.map()` immediately fires N parallel
    // network requests regardless of what we pass to Promise.allSettled.
    const profileTaskFactories = contacts
      .map((c) => c?.contactId ?? c?.contact_id ?? c?.id)
      .filter(Boolean)
      .map(
        (contactId) => () =>
          queryClient.prefetchQuery({
            queryKey: ["profile", contactId, primaryCampaignId, userId],
            queryFn: () =>
              fetchProfileApi({
                contact_id: contactId,
                campaign_id: primaryCampaignId,
                user_id: userId,
              }),
          }),
      );

    void runWithConcurrency(PROFILE_FETCH_CONCURRENCY, profileTaskFactories);
  }
}

/**
 * Example searches written from the user's company profile ("contacts" = people to find,
 * "accounts" = companies to find). `personalized` is false (and the list empty) when the
 * company has no profile yet, or the request failed, so callers show their defaults.
 */
export const useSearchSuggestions = (kind) => {
  const userId = useMainStore((state) => state.user?.id);
  const query = useQuery({
    queryKey: ["search-suggestions", userId, kind],
    queryFn: () => fetchSearchSuggestions({ user_id: userId, kind }),
    enabled: Boolean(userId),
    // The server caches for a day and refreshes when the company profile changes.
    staleTime: 60 * 60 * 1000,
    retry: 0,
    // A nicety, never an error toast: the page just shows its defaults.
    meta: { silentError: true },
  });
  const suggestions = Array.isArray(query.data?.suggestions) ? query.data.suggestions : [];
  return {
    suggestions,
    isLoading: query.isLoading,
    personalized: Boolean(query.data?.personalized) && suggestions.length > 0,
  };
};

export const useContactSearchParams = () => {
  return useMutation({
    mutationFn: fetchContactSearchParams,
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.response?.data?.detail || error.message || "Failed to generate search parameters.",
      );
    },
  });
};

/** Free availability check; on failure the search just shows everyone (no error toast). */
export const useContactAvailability = () =>
  useMutation({ mutationFn: fetchContactAvailability, meta: { silentError: true } });

export const useAiPeopleSearch = () =>
  useMutation({
    mutationFn: aiPeopleSearch,
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.code === "ECONNABORTED"
          ? "The search took too long. Try a narrower query."
          : error.response?.data?.detail || error.message || "Failed to search contacts.",
      );
    },
  });

export const useProspectSearchCampaignQueryDraft = () =>
  useMutation({
    mutationFn: draftProspectSearchQueryFromCampaign,
  });

export const useProspectByQuery = () => {
  return useMutation({
    mutationFn: fetchProspectsByQuery,
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.response?.data?.error || error.message || "Failed to search prospects.",
      );
    },
  });
};

export const useProspectSearchQuery = () =>
  useMutation({
    mutationFn: searchProspectsByNaturalQuery,
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          error.message ||
          "Prospect search failed.",
      );
    },
  });

export const useProspectSearchCampaignIcp = () =>
  useMutation({
    mutationFn: searchProspectsByCampaignIcp,
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          error.message ||
          "Campaign ICP prospect search failed.",
      );
    },
  });

export const useProspectLoadMore = () =>
  useMutation({
    mutationFn: loadMoreProspects,
    onError: (error) => {
      const status = error.response?.status;
      const code =
        error.response?.data?.error || error.response?.data?.code || "";
      showErrorFromMutation(
        error,
        status === 410 || code === "session_expired"
          ? error.response?.data?.message ||
              "This search session has expired. Please run the search again."
          : error.response?.data?.detail?.message ||
              error.response?.data?.message ||
              error.message ||
              "Could not load more results.",
      );
    },
  });

export const useEnrichProspects = () =>
  useMutation({
    mutationFn: async () => {
      useEnrichmentModalStore.getState().openModal();
      throw new Error("Enrichment is an Enterprise plan feature.");
    },
    onError: () => {},
  });

export const useImportFromContactOut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: importFromContactOut,
    onSuccess: async (data, variables) => {
      await runBulkWorkspaceImportSideEffects(queryClient, data, variables);
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.response?.data?.error || error.message || "Failed to import contacts.",
      );
    },
  });
};

export const useImportProspectSearchEnriched = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: importProspectSearchEnrichedContacts,
    onSuccess: async (data, variables) => {
      await runBulkWorkspaceImportSideEffects(queryClient, data, variables);
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.response?.data?.error ||
          error.message ||
          "Failed to save imported contacts.",
      );
    },
  });
};

export const useProspectSearchThreads = ({ userId, enabled = true }) =>
  useQuery({
    queryKey: ["prospectSearchThreads", userId],
    queryFn: () => fetchProspectSearchThreads({ user_id: userId }),
    enabled: Boolean(userId) && enabled,
    retry: false,
  });

export const useProspectSearchThreadMessages = ({
  userId,
  threadId,
  enabled = true,
}) =>
  useQuery({
    queryKey: ["prospectSearchThreadMessages", userId, threadId],
    queryFn: () =>
      fetchProspectSearchThreadMessages({
        user_id: userId,
        thread_id: threadId,
      }),
    enabled: Boolean(userId && threadId) && enabled,
    retry: false,
  });

export const useCreateProspectSearchThread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProspectSearchThread,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["prospectSearchThreads", variables.user_id],
      });
    },
  });
};

export const useDeleteProspectSearchThread = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProspectSearchThread,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["prospectSearchThreads", variables.user_id],
      });
    },
  });
};

export const useSendProspectSearchChatMessage = () =>
  useMutation({
    mutationFn: async ({ onStreamChunk, ...payload }) => {
      const stream = await sendProspectSearchChatMessage(payload);
      if (!stream || !onStreamChunk) return { streamed: true };

      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              onStreamChunk(JSON.parse(line));
            } catch (error) {
              error.message = `Failed to parse prospect-search stream chunk: ${error.message}`;
              error.rawChunk = line;
              throw error;
            }
          }
        }
        if (buffer.trim()) {
          try {
            onStreamChunk(JSON.parse(buffer));
          } catch (error) {
            error.message = `Failed to parse prospect-search stream chunk: ${error.message}`;
            error.rawChunk = buffer;
            throw error;
          }
        }
      } finally {
        reader.releaseLock();
      }
      return { streamed: true };
    },
  });
